import { z } from 'zod';
import { normalizeEncounterTier } from '@/IsekaiRPG/modules/character/characterSemantic';
import { CharacterSpawnRequestSchema } from '@/IsekaiRPG/modules/character/characterSpawnTypes';
import { ItemAcquisitionPlanSchema } from '@/IsekaiRPG/modules/item/itemTypes';
import { AISkillPayloadSchema } from '@/IsekaiRPG/modules/skill/skillTypes';
import { SiteEncounterSeedCandidateSchema } from './siteEncounterAdjustment';

// ============================================
// 探索据点下游内容契约（角色/技能/物品）
// ============================================

const OptionalBindingIdSchema = z.preprocess(value => {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    const nextValue = value.trim();
    return nextValue.length > 0 ? nextValue : undefined;
  }
  return value;
}, z.string().min(1).optional());

export const SiteCharacterEncounterTierSchema = z.preprocess(
  value => normalizeEncounterTier(typeof value === 'string' ? value : undefined),
  z.enum(['杂兵', '精英', '首领']),
);
export type SiteCharacterEncounterTier = z.infer<typeof SiteCharacterEncounterTierSchema>;

const BaseSiteCharacterSpawnEntrySchema = z.object({
  spawnId: z.string().optional(),
  seedDefinitionId: z.string().min(1).optional(),
  request: CharacterSpawnRequestSchema,
  /** 遭遇层级：用于刷怪规模、房间优先级与实体化强度。 */
  encounterTier: SiteCharacterEncounterTierSchema.default('杂兵'),
  bindRoomId: OptionalBindingIdSchema,
  isUniqueRunContent: z.boolean().default(false),
  guaranteedSpawnCount: z.number().int().min(1).optional(),
  skills: z.array(AISkillPayloadSchema).default([]),
});

/**
 * 据点角色生成项（探索侧通常为敌对）：
 * - request: 统一角色生成请求
 * - bindRoomId: 绑定到指定房间（可选）
 * - isUniqueRunContent: 是否仅本轮使用
 * - skills: 角色内联技能草案（可选，用于据点生成器补全战斗能力）
 * - guaranteedSpawnCount: 该草案在其绑定房间中需要保底生成的数量
 */
export const SiteCharacterSpawnEntrySchema = BaseSiteCharacterSpawnEntrySchema;
export type SiteCharacterSpawnEntry = z.infer<typeof SiteCharacterSpawnEntrySchema>;

export function resolveSiteCharacterEncounterTier(
  entry: Pick<SiteCharacterSpawnEntry, 'encounterTier'>,
): SiteCharacterEncounterTier {
  return entry.encounterTier;
}

/**
 * 据点物品计划：探索模块只维护房间/角色绑定，物品取得方式由 item 模块统一建模。
 */
export const SiteItemDraftSchema = z
  .object({
    itemId: z.string().optional(),
    plan: ItemAcquisitionPlanSchema,
    bindRoomId: OptionalBindingIdSchema,
    bindCharacterId: OptionalBindingIdSchema,
  })
  .strict();
export type SiteItemDraft = z.infer<typeof SiteItemDraftSchema>;

/**
 * 据点内容计划集合（用于一次批量生成/解析）
 *
 * 注意：
 * - schemaVersion/sourceRunId 由系统自动注入，不要求 AI 输出。
 */
export const SiteContentPlanSchema = z.object({
  schemaVersion: z.literal('2.0.0').default('2.0.0'),
  sourceRunId: z.string().optional(),
  characterSpawns: z.array(SiteCharacterSpawnEntrySchema).default([]),
  seedCandidates: z.array(SiteEncounterSeedCandidateSchema).default([]),
  items: z.array(SiteItemDraftSchema).default([]),
});
export type SiteContentPlan = z.infer<typeof SiteContentPlanSchema>;
